"use client";

import { useState, useEffect, useRef, startTransition } from "react";
import { searchPlaces } from "@/lib/api/misc";
import type { PlaceResult } from "@/lib/api/misc";

export interface SavedCity {
  name: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  country?: string;
}

interface TimingCitySelectorProps {
  city: SavedCity;
  onCitySelect: (city: SavedCity) => void;
  countryFilter?: string;
}

export function TimingCitySelector({ city, onCitySelect, countryFilter }: TimingCitySelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<PlaceResult[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced city search
  useEffect(() => {
    if (searchQuery.length < 2) {
      startTransition(() => setSearchResults([]));
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchPlaces(searchQuery, 5, countryFilter);
        startTransition(() => setSearchResults(results));
      } catch (err) {
        console.error("Failed to search places", err);
      }
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [countryFilter, searchQuery]);

  function handleSelect(place: PlaceResult) {
    onCitySelect({
      name: place.name,
      latitude: place.latitude,
      longitude: place.longitude,
      timezone: place.timezone,
      country: place.country,
    });
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
  }

  return (
    <div className="mt-3 relative inline-block">
      <button
        onClick={() => setShowSearch(!showSearch)}
        className="text-[13px] font-medium px-3 py-1.5 rounded-lg transition-colors text-ish-accent border border-ish-border-light bg-black/5"
      >
        {city.name}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="inline-block ml-1.5 -mt-0.5"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {showSearch && (
        <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 w-72 rounded-xl shadow-lg z-20 overflow-hidden bg-ish-surface border border-ish-border-light">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search city..."
            autoFocus
            className="w-full px-4 py-3 text-[14px] outline-none bg-transparent text-ish-dark border-b border-ish-border-light"
          />
          {searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              {searchResults.map((place) => (
                <button
                  key={place.geoname_id}
                  onClick={() => handleSelect(place)}
                  className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-stone-50 transition-colors text-ish-dark"
                >
                  {place.display}
                </button>
              ))}
            </div>
          )}
          {searchQuery.length >= 2 && searchResults.length === 0 && (
            <p className="px-4 py-3 text-[13px] text-ish-text-faint">No cities found</p>
          )}
        </div>
      )}
    </div>
  );
}

// Compatibility export: the selector began inside Choghadiya, but timing tools
// now share it. Existing imports keep working while new surfaces use the
// capability-level name.
export const ChoghadiyaCitySelector = TimingCitySelector;
